/**
 * Shared CSV helpers.
 *
 * Quoting follows RFC 4180:
 *   - Fields containing `"`, `,`, `\n` or `\r` are wrapped in double quotes.
 *   - Embedded double quotes are escaped by doubling them.
 *   - `null` and `undefined` render as empty strings.
 *
 * Originally extracted from `features/security/AuditLogPage.tsx` so the
 * pre-built `ReportShell` can share the same escaping rules for client-side
 * downloads.
 */

export type CsvCell = string | number | boolean | null | undefined;

/** Escape a single CSV cell per RFC 4180. */
export function escapeCsvCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Convert a list of records into CSV text. Column order is the union of all
 * keys observed across rows, with the first row's keys preserved first.
 *
 * Returns an empty string when `rows` is empty.
 */
export function rowsToCsv(rows: readonly Record<string, unknown>[]): string {
  if (rows.length === 0) return '';
  const cols: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        cols.push(key);
      }
    }
  }
  const header = cols.map((c) => escapeCsvCell(c)).join(',');
  const body = rows
    .map((r) => cols.map((c) => escapeCsvCell(r[c])).join(','))
    .join('\n');
  return `${header}\n${body}\n`;
}

/**
 * Convert an arbitrary JSON value into CSV.
 *
 * Strategy:
 *   - If the value is itself an array of objects, render it as one CSV table.
 *   - If the value is an object, find every top-level property that is an
 *     array of objects and render them as separate sections, each preceded
 *     by a single-cell "## <property>" section header. Non-array primitive
 *     properties are emitted at the top under a "metric,value" header.
 *   - Anything else (primitive, empty object) yields a single-cell CSV with
 *     the JSON-stringified value.
 *
 * Always returns a string; never throws.
 */
export function jsonToCsv(value: unknown): string {
  if (Array.isArray(value)) {
    const rows = value.filter((v): v is Record<string, unknown> =>
      typeof v === 'object' && v !== null && !Array.isArray(v),
    );
    if (rows.length === 0) return '';
    return rowsToCsv(rows);
  }

  if (value === null || typeof value !== 'object') {
    return `${escapeCsvCell(value)}\n`;
  }

  const obj = value as Record<string, unknown>;
  const sections: string[] = [];

  // First, flat scalars + small primitive arrays as a "summary" table.
  const scalars: Record<string, unknown>[] = [];
  for (const [key, v] of Object.entries(obj)) {
    if (Array.isArray(v) && v.some((x) => typeof x === 'object' && x !== null)) continue;
    scalars.push({ metric: key, value: v });
  }
  if (scalars.length > 0) {
    sections.push(rowsToCsv(scalars));
  }

  // Then, every property that is an array of records becomes its own section.
  for (const [key, v] of Object.entries(obj)) {
    if (!Array.isArray(v)) continue;
    const records = v.filter((x): x is Record<string, unknown> =>
      typeof x === 'object' && x !== null && !Array.isArray(x),
    );
    if (records.length === 0) continue;
    sections.push(`\n## ${escapeCsvCell(key)}\n${rowsToCsv(records)}`);
  }

  return sections.join('');
}

/** Trigger a browser download for the given content + filename. No-op in SSR. */
export function downloadBlob(filename: string, content: string, mime: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
